import encodeQr from './qrcode';

/**
 * Save the QR code as a PNG, in one click.
 *
 * ── Why this re-draws the code instead of saving the one on screen ───────
 *
 * The code on screen is about 240 pixels across, because that is a sensible
 * size to look at. Saved at that size and then blown up to fit a table card,
 * every square gets smeared into grey by whatever is doing the scaling, and a
 * camera stops being able to lock on. It is the usual reason a printed QR
 * code will not read.
 *
 * So the file is drawn fresh, from the same string, at about 1200 pixels. It
 * is the same code — the encoder is deterministic, so the same link always
 * gives the same squares — just with enough pixels to survive being printed,
 * pasted into a poster, or sent through WhatsApp, which re-compresses
 * everything it touches.
 *
 * ── The three things that keep it scannable ──────────────────────────────
 *
 *   Whole modules. The module size is a whole number of pixels, so no square
 *   is ever a pixel wider than its neighbour and none of them land on a half
 *   pixel with a grey edge.
 *
 *   The quiet zone. Four empty modules all the way round, drawn into the
 *   image itself. It is how a camera works out where the code ends, and
 *   putting it inside the file means no layout it gets dropped into can crop
 *   it off.
 *
 *   Pure black on pure white. The code on screen uses the app's softer ink,
 *   which is right on a screen. A file that may end up on a photocopier or in
 *   a compressed image wants the most contrast there is.
 */

const QUIET = 4;

/** Roughly how wide the saved image should be. Rounded to whole modules. */
const TARGET_PX = 1200;

/**
 * `xamar-restaurant-menu-qr.png` rather than `download.png`, because a shop
 * with three branches ends up with three of these in one folder.
 */
function fileNameFor(businessName) {
  const slug = String(businessName == null ? '' : businessName)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/, '');

  return `${slug ? `${slug}-` : ''}menu-qr.png`;
}

/**
 * Hand a file to the browser. No new window, no dialog — the anchor is made,
 * clicked and thrown away, and the browser puts the PNG in the downloads
 * folder the way it would any other download.
 */
function saveAs(href, fileName, afterwards) {
  const link = document.createElement('a');
  link.href = href;
  link.download = fileName;
  link.rel = 'noopener';
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // The browser has taken a reference by the time click() returns, but some
  // of them read the blob lazily, so the URL is kept alive for a while rather
  // than revoked from under a download that is still starting.
  if (afterwards) window.setTimeout(afterwards, 20000);
}

export default function downloadQrPng({ value, businessName }) {
  if (!value) return false;

  let code;
  try {
    code = encodeQr(value);
  } catch (err) {
    return false;
  }

  const total = code.size + QUIET * 2;
  const modulePx = Math.max(10, Math.floor(TARGET_PX / total));
  const side = modulePx * total;

  const fileName = fileNameFor(businessName);

  try {
    const canvas = document.createElement('canvas');
    canvas.width = side;
    canvas.height = side;

    const ctx = canvas.getContext('2d');
    if (!ctx) return false;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, side, side);

    ctx.fillStyle = '#000000';
    for (let r = 0; r < code.size; r += 1) {
      for (let c = 0; c < code.size; c += 1) {
        if (!code.modules[r][c]) continue;
        ctx.fillRect((c + QUIET) * modulePx, (r + QUIET) * modulePx, modulePx, modulePx);
      }
    }

    /**
     * A blob is preferred over a data URL: a 1200px PNG is a couple of
     * hundred kilobytes, and as base64 in an href that is a third bigger
     * again and has to be parsed as a URL. The data URL is kept as the
     * fallback for anything that cannot do toBlob.
     */
    if (typeof canvas.toBlob === 'function') {
      canvas.toBlob((blob) => {
        if (!blob) {
          try {
            saveAs(canvas.toDataURL('image/png'), fileName);
          } catch (err) {
            /* nothing left to try */
          }
          return;
        }
        const url = URL.createObjectURL(blob);
        saveAs(url, fileName, () => URL.revokeObjectURL(url));
      }, 'image/png');
    } else {
      saveAs(canvas.toDataURL('image/png'), fileName);
    }

    return true;
  } catch (err) {
    return false;
  }
}
